// Everything marketing sends or posts, by day: social posts (planned and
// shared), email and SMS campaigns (scheduled and sent), UK dates to know and
// Bailey's suggestions. One list, so the Social page, Marketing home and the
// Calendar page all agree.
import moment from 'moment'
import { marketingApiService } from '../../../services/marketingApiService'
import { momentsBetween } from '../../social/data/seasonal'
import { listPosts } from '../../social/data/postsApi'
import { isRemote } from './siteScope'

let cache = {} // range key -> { at, list }
const FRESH_MS = 5 * 60 * 1000

// "2026-10-03" + "10:30" (no timezone: the salon's local time)
const scheduledAt = (c) => {
  if (!c.scheduledDate) return null
  const day = moment.utc(c.scheduledDate)
  if (!day.isValid()) return null
  const at = moment(day.format('YYYY-MM-DD'))
  const [h, m] = String(c.scheduledTime || '').split(':').map(Number)
  if (Number.isFinite(h)) at.hours(h).minutes(Number.isFinite(m) ? m : 0)
  return at.toDate()
}

// Where a campaign sits on the calendar. Sent: when it went. Scheduled, and
// skipped (a gap-filling text called off because the times filled): when it
// was due. The backend's from/to matches on the same dates.
const placed = (c) => {
  if (c.status === 'sent') return { status: 'sent', at: c.sentDate ? new Date(c.sentDate) : null }
  if (c.status === 'skipped') return { status: 'skipped', at: scheduledAt(c) }
  if (c.status === 'scheduled' || c.status === 'sending') return { status: 'scheduled', at: c.status === 'sending' && c.sentDate ? new Date(c.sentDate) : scheduledAt(c) }
  return null // drafts, failed, archived: not on the calendar
}

// Every email and text campaign between two dates: one call per channel with
// the date range, so nothing past a first page is missed. Without dates:
// the last five weeks and the next two months (what Home reads).
// salonIds (from siteScope's salonIdsParam): the Locations filter; absent
// means this location only, exactly the request it always was.
export const loadCampaigns = async (dispatch, { from, to, force = false, salonIds = null } = {}) => {
  const start = moment(from || moment().subtract(35, 'days')).format('YYYY-MM-DD')
  const end = moment(to || moment().add(62, 'days')).format('YYYY-MM-DD')
  const key = `${start}_${end}_${salonIds || ''}`
  const hit = cache[key]
  if (!force && hit && Date.now() - hit.at < FRESH_MS) return hit.list
  const calls = ['email', 'sms'].map(type => marketingApiService.getCampaignsPaged(dispatch, { type, from: start, to: end, ...(salonIds ? { salonIds } : {}) })
    .then(r => (r.campaigns || []).map(c => ({ ...c, channel: type })))
    .catch(() => []))
  const list = (await Promise.all(calls)).flat()
    .map((c) => {
      const where = placed(c)
      if (!where) return null
      return {
        id: c.id || c._id,
        kind: c.channel,
        status: where.status,
        at: where.at,
        title: c.name || (c.channel === 'sms' ? 'Text' : 'Email'),
        audienceSize: c.audienceSize || null,
        stats: c.stats || null,
        held: !!c.heldReason,
        source: c.source || null,
        skipReason: c.skipReason || null,
        salonId: (c._raw && c._raw.salonId) || null,
        siteName: (c._raw && c._raw.siteName) || null,
        remote: isRemote(c._raw && c._raw.salonId),
      }
    })
    .filter(c => c && c.at && !Number.isNaN(c.at.getTime()))
    .sort((a, b) => a.at - b.at)
  cache[key] = { at: Date.now(), list }
  return list
}
export const forgetCampaigns = () => { cache = {} }

// The other locations' posts for the Locations filter, read only. This
// location's posts come from the social store (they can be edited); only the
// rest are fetched here, so nothing from another site enters that store.
export const loadSitePosts = async ({ from, to, salonIds } = {}) => {
  if (!salonIds) return []
  const r = await listPosts({ from: moment(from).startOf('day').toISOString(), to: moment(to).endOf('day').toISOString(), salonIds, limit: 500 })
  return (r.posts || []).filter(p => isRemote(p.salonId)).map(p => ({ ...p, remote: true }))
}

export const dayKey = (d) => moment(d).format('YYYY-MM-DD')
export const FILTERS = [
  { id: 'all', label: 'Everything' },
  { id: 'posts', label: 'Posts' },
  { id: 'emails', label: 'Emails' },
  { id: 'texts', label: 'Texts' },
]
const passes = (filter, kind) => filter === 'all' || kind === 'moment' || kind === 'idea'
  || (filter === 'posts' && (kind === 'post' || kind === 'story'))
  || (filter === 'emails' && kind === 'email')
  || (filter === 'texts' && kind === 'sms')

// Items between two dates, grouped by day and in time order
//   posts: social posts; campaigns: from loadCampaigns; plans: Bailey's
//   suggested items [{ id, planId, kind, at, title }]
export const itemsByDay = ({ from, to, posts = [], campaigns = [], plans = [], trade, filter = 'all', moments = true }) => {
  const start = moment(from).startOf('day')
  const end = moment(to).endOf('day')
  const inRange = (d) => d && moment(d).isBetween(start, end, null, '[]')
  const out = {}
  const add = (item) => {
    if (!inRange(item.at) || !passes(filter, item.kind)) return
    const k = dayKey(item.at)
    ;(out[k] = out[k] || []).push(item)
  }
  posts.forEach((p) => {
    const at = p.status === 'posted' ? p.postedAt : p.plannedAt
    if (!at) return
    add({ key: `p-${p.id}`, kind: p.format === 'story' ? 'story' : 'post', status: p.status === 'posted' ? 'posted' : 'planned', at: new Date(at), title: p.name || '', post: p,
      ...(p.remote ? { remote: true, salonId: p.salonId, siteName: p.siteName || null } : {}) })
  })
  campaigns.forEach(c => add({ ...c, key: `c-${c.kind}-${c.id}`, campaign: c }))
  plans.forEach(it => add({ ...it, key: `i-${it.id}`, kind: 'idea', status: 'suggested' }))
  if (moments) {
    momentsBetween(start.toDate(), end.toDate(), trade).forEach(m => add({ key: `m-${m.key}`, kind: 'moment', at: m.date, title: m.name, moment: m, allDay: true }))
  }
  Object.keys(out).forEach((k) => {
    out[k].sort((a, b) => (a.allDay ? -1 : b.allDay ? 1 : a.at - b.at))
  })
  return out
}

// The days a view shows
export const monthDays = (anchor) => {
  const first = moment(anchor).startOf('month').startOf('isoWeek')
  const last = moment(anchor).endOf('month').endOf('isoWeek')
  const days = []
  for (let d = first.clone(); d.isSameOrBefore(last, 'day'); d.add(1, 'day')) days.push(d.clone())
  return days
}
export const weekDays = (anchor) => {
  const first = moment(anchor).startOf('isoWeek')
  return Array.from({ length: 7 }, (_, i) => first.clone().add(i, 'day'))
}

// What an item is, in a few words
export const KIND_NAMES = { post: 'Post', story: 'Story', email: 'Email', sms: 'Text', moment: 'Date to know', idea: 'Suggested' }
export const itemTime = (it) => (it.allDay ? '' : moment(it.at).format('h:mma').replace(':00', ''))

// Campaigns as bands across their days: the salon's own (drafts and
// approved) and Bailey's suggestions. [{ id, title, start, end, kind, planKey?, campaignId? }]
export const campaignBands = ({ campaigns = [], plans = [] } = {}) => {
  const out = []
  const made = new Set()
  campaigns.forEach((c) => {
    if (!c || !c.start || !c.end || c.status === 'cancelled') return
    if (c.planKey) made.add(c.planKey)
    out.push({ id: `c:${c.id}`, title: c.name || c.title || 'Campaign', start: c.start, end: c.end, kind: c.status === 'approved' ? 'campaign' : 'draft', campaignId: c.id })
  })
  plans.forEach((p) => {
    if (!p || !p.start || !p.end || made.has(p.key) || p.start === p.end) return
    out.push({ id: `p:${p.key}`, title: p.title, start: p.start, end: p.end, kind: 'suggested', planKey: p.key })
  })
  // Each band keeps one row (lane) all the way across, so a long one never
  // jumps up or down between days
  const ends = []
  return out.sort((a, b) => a.start.localeCompare(b.start) || b.end.localeCompare(a.end)).map((b) => {
    let lane = ends.findIndex(e => e < b.start)
    if (lane === -1) lane = ends.length
    ends[lane] = b.end
    return { ...b, lane }
  })
}

// The bands on a day, each saying whether it starts or ends there
export const bandsOn = (bands = [], day) => {
  const d = moment(day).format('YYYY-MM-DD')
  return bands.filter(b => b.start <= d && b.end >= d).map(b => ({ ...b, isStart: b.start === d, isEnd: b.end === d }))
}
